// Czyste formatery tekstu na podstawie market_stats z API.
// Front NIE liczy żadnych danych rynkowych (mediany, trendu, dostępności,
// ceny jednostkowej, najniższej ceny) — tylko formatuje do czytelnego tekstu
// wartości, które backend już wyliczył. Używane przez MarketStats.jsx (F1)
// i MarketFaq.jsx (F2), żeby oba miejsca pokazywały identyczny tekst.
// Teksty w słowniku `model` (rynek.*); i18n.t, bo to nie komponent Reacta.
import { formatPrice, formatKwota } from '../utils';
import i18n from '../i18n';

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

export const priceRangeText = (ms) => {
  if (!ms) return null;
  const { price_min, price_max, price_on_request } = ms;
  const hasRange = hasValue(price_min) && hasValue(price_max) && Number(price_min) > 0 && Number(price_max) > 0;
  if (hasRange) {
    const min = formatKwota(price_min, 0);
    const max = formatKwota(price_max, 0);
    if (min === null || max === null) return null;
    return Number(price_min) === Number(price_max) ? min : i18n.t('model:rynek.zakres_cen', { min, max });
  }
  if (price_on_request && hasValue(price_on_request.distributors)) {
    return i18n.t('model:rynek.cena_na_zapytanie', { liczba: price_on_request.distributors });
  }
  return null;
};

export const cheapestSellerText = (ms) => (ms && hasValue(ms.cheapest_seller) ? ms.cheapest_seller : null);

export const cheapestOfferUrl = (ms) => (ms && hasValue(ms.cheapest_offer_url) ? ms.cheapest_offer_url : null);

export const offerCountText = (ms) => {
  if (!ms || !hasValue(ms.offer_count)) return null;
  const n = Number(ms.offer_count);
  if (Number.isNaN(n)) return null;
  return i18n.t('model:rynek.oferty_licznik', { count: n });
};

export const asOfText = (ms) => (ms && hasValue(ms.as_of_display) ? ms.as_of_display : null);

export const unitPriceText = (ms) => {
  const up = ms?.unit_price;
  if (!up || !hasValue(up.value) || !hasValue(up.unit)) return null;
  const val = formatKwota(up.value, up.decimals ?? 2);
  return val !== null ? `${val}/${up.unit}` : null;
};

// Z czym porównujemy (backend `market_stats.py`, `_segment_median`): modele o zbliżonym parametrze
// głównym i, gdzie trzeba, tym samym typie. Słowo „segment” było niezrozumiałe (uwaga właściciela 2026-10-08).
// Teksty w słowniku `model` (rynek.podobne.*); nieznana kategoria to „podobnych modeli”.
const PODOBNE = ['panel', 'inwerter', 'magazyn', 'wallbox'];

/** „13,1% taniej niż mediana 24 modułów o zbliżonej mocy (±25 W)”. Bez znaku minus: kierunek mówi słowo. */
export const segmentMedianText = (ms, kategoria) => {
  const sm = ms?.segment_median;
  if (!sm || !hasValue(sm.pct_diff) || sm.cheaper === null || sm.cheaper === undefined) return null;
  const pct = formatPrice(Math.abs(Number(sm.pct_diff)), 1);
  if (pct === null) return null;
  const n = Number(sm.count);
  const ile = !hasValue(sm.count) ? ''
    : Number.isFinite(n) ? i18n.t('model:rynek.ile_modeli', { count: n }) : `${sm.count} `;
  const czego = i18n.t(`model:rynek.podobne.${PODOBNE.includes(kategoria) ? kategoria : 'inne'}`);
  if (Math.abs(Number(sm.pct_diff)) < 0.05) return i18n.t('model:rynek.mediana_zblizona', { ile, czego });
  return i18n.t(sm.cheaper ? 'model:rynek.taniej_niz_mediana' : 'model:rynek.drozej_niz_mediana', { pct, ile, czego });
};

export const trend30dText = (ms) => {
  const t = ms?.trend_30d;
  if (!t || !hasValue(t.direction) || !hasValue(t.pct)) return null;
  const pct = formatPrice(t.pct, 1);
  if (pct === null) return null;
  // Kierunek przychodzi z backendu po polsku („spadła” / „wzrosła”, market_stats.py).
  const klucz = { 'spadła': 'trend_spadla', 'wzrosła': 'trend_wzrosla' }[t.direction] || 'trend_inny';
  return i18n.t(`model:rynek.${klucz}`, { pct, kierunek: t.direction });
};

// P3: historia ceny. Backend liczy (najniższa cena + data, mediany, czy dziś jest
// minimum) — tu tylko formatowanie. Te same fakty pokazuje wersja dla bota
// (seo_render._s_history), żeby treść widoczna zgadzała się z tą dla Google.
export const priceHistoryText = (ms) => {
  const h = ms?.price_history;
  if (!h || !hasValue(h.min_ever)) return null;
  if (h.at_historical_low) {
    return h.since_date_display
      ? i18n.t('model:rynek.najnizsza_od', { data: h.since_date_display })
      : i18n.t('model:rynek.najnizsza');
  }
  const lo = formatKwota(h.min_ever, 0);
  if (lo === null) return null;
  return h.min_ever_date_display
    ? i18n.t('model:rynek.minimum_data', { kwota: lo, data: h.min_ever_date_display })
    : i18n.t('model:rynek.minimum', { kwota: lo });
};

export const availabilityText = (ms) => {
  const a = ms?.availability;
  if (!a || !hasValue(a.total) || Number(a.total) === 0 || !hasValue(a.available)) return null;
  return i18n.t('model:rynek.dostepny_w', { dostepne: a.available, wszystkie: a.total });
};
